import { trace } from '@opentelemetry/api';
import {
  logs,
  SeverityNumber,
  type LogAttributes,
} from '@opentelemetry/api-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  BatchLogRecordProcessor,
  LoggerProvider,
  type LogRecordExporter,
} from '@opentelemetry/sdk-logs';
import {
  ATTR_CLIENT_ADDRESS,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  ATTR_URL_FULL,
} from '@opentelemetry/semantic-conventions';
import { ATTR_USER_ID } from '@opentelemetry/semantic-conventions/incubating';

import { isBrowser } from '@shared-web/utils/runtime';

import type { ClientTelemetryConfig } from '../config';
import { userContext } from './user-context';

declare global {
  interface Window {
    correlationId?: string;
  }
}

let loggerProvider: LoggerProvider | null = null;
let originalConsoleError: typeof console.error | null = null;
let isEmittingLog = false;
let consoleLoggingEnabled: boolean | null = null;

export function setupLogging(config: ClientTelemetryConfig): void {
  if (config.debug) {
    console.log('[OpenTelemetry] Setting up logging with config:', {
      logsEndpoint: config.logsEndpoint,
      debug: config.debug,
      environment: config.environment,
    });
  }

  if (loggerProvider) {
    console.warn('[OpenTelemetry] Logging already initialized');
    return;
  }

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
    [ATTR_SERVICE_VERSION]: config.serviceVersion,
    'deployment.environment': config.environment,
  });

  const exporter = createLogExporter(config);

  if (config.debug) {
    console.log(
      '[OpenTelemetry] Created log exporter for:',
      config.logsEndpoint,
    );
  }

  const batchProcessor = new BatchLogRecordProcessor(exporter, {
    maxExportBatchSize: 50,
    exportTimeoutMillis: 30 * 1000,
    scheduledDelayMillis: 3 * 1000,
  });

  loggerProvider = new LoggerProvider({
    resource,
    processors: [batchProcessor],
  });

  logs.setGlobalLoggerProvider(loggerProvider);
  consoleLoggingEnabled = config.enableConsoleLogging ?? false;

  if (config.debug) {
    console.log('[OpenTelemetry] Logging initialized successfully');
  }
}

/**
 * Creates the OTLP log exporter.
 *
 * Note: No accessToken header is needed here - the BFF proxy handles
 * authentication for logs endpoint. Unlike metrics/traces which may go to
 * different backends requiring auth tokens, logs are routed through our
 * internal proxy which manages credentials.
 */
function createLogExporter(config: ClientTelemetryConfig): LogRecordExporter {
  return new OTLPLogExporter({
    url: config.logsEndpoint,
  });
}

export function getLogger(name: string = 'default') {
  if (!loggerProvider) {
    if (shouldLogToConsole()) {
      console.warn(
        '[OpenTelemetry] Logging not initialized, using console fallback',
      );
    }
    return createFallbackLogger();
  }

  return loggerProvider.getLogger(name);
}

interface FallbackLogRecord {
  severityNumber?: SeverityNumber;
  body?: string;
  attributes?: Record<string, unknown>;
}

function createFallbackLogger() {
  return {
    emit: (logRecord: FallbackLogRecord) => {
      // Fallback logger respects enableConsoleLogging setting
      if (!shouldLogToConsole()) return;

      const level = getSeverityText(logRecord.severityNumber);
      const message = logRecord.body || 'No message';
      const attributes = logRecord.attributes
        ? JSON.stringify(logRecord.attributes)
        : '';

      console.log(`[${level}] ${message}${attributes ? ` ${attributes}` : ''}`);
    },
  };
}
/**
 * https://opentelemetry.io/docs/specs/otel/logs/data-model/#field-severitynumber
 * we only support the following levels: DEBUG, INFO, WARN, ERROR
 */
const LOG_LEVELS = {
  DEBUG: { severity: SeverityNumber.DEBUG, text: 'DEBUG' },
  INFO: { severity: SeverityNumber.INFO, text: 'INFO' },
  WARN: { severity: SeverityNumber.WARN, text: 'WARN' },
  ERROR: { severity: SeverityNumber.ERROR, text: 'ERROR' },
} as const;

type LogLevel = keyof typeof LOG_LEVELS;

/**
 * Minimum log severity to upload to OTEL per environment.
 * Logs below this severity will only go to console (if enabled), not OTEL.
 */
const MIN_UPLOAD_SEVERITY: Record<string, SeverityNumber> = {
  development: SeverityNumber.DEBUG,
  staging: SeverityNumber.DEBUG,
  production: SeverityNumber.INFO,
};

/**
 * Get the application environment for log filtering.
 * Priority: VITE_APP_ENV > MODE (from --mode flag)
 * - `pnpm dev` → MODE = 'development'
 * - `pnpm build --mode staging` → MODE = 'staging'
 * - `pnpm build --mode production` → MODE = 'production'
 */
function getAppEnv(): string {
  return import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'production';
}

function shouldUploadLog(severity: SeverityNumber): boolean {
  const appEnv = getAppEnv();
  const minSeverity = MIN_UPLOAD_SEVERITY[appEnv] ?? SeverityNumber.INFO;

  return severity >= minSeverity;
}

function getSeverityText(severityNumber?: SeverityNumber): string {
  if (!severityNumber) return LOG_LEVELS.INFO.text;

  if (severityNumber >= SeverityNumber.ERROR) return LOG_LEVELS.ERROR.text;
  if (severityNumber >= SeverityNumber.WARN) return LOG_LEVELS.WARN.text;
  if (severityNumber >= SeverityNumber.INFO) return LOG_LEVELS.INFO.text;
  return LOG_LEVELS.DEBUG.text;
}

function shouldLogToConsole(): boolean {
  return consoleLoggingEnabled === true;
}

/**
 * Convenience logging functions that emit logs to both:
 * 1. OpenTelemetry LoggerProvider (for centralized observability)
 * 2. Browser console (when `enableConsoleLogging: true` is passed to setupTelemetry)
 *
 * These functions also receive logs from the Market WebSocket SharedWorker,
 * which cannot directly access OpenTelemetry due to limited Web Worker APIs.
 * The worker sends log messages to the main thread via postMessage, which
 * are then processed through these functions.
 *
 * @example
 * ```typescript
 * // Basic usage
 * logger.info('User logged in', { userId: '123' });
 *
 * // Error with exception
 * logger.error('API call failed', error, { endpoint: '/api/users' });
 * ```
 */
function getUserContextAttributes(): LogAttributes {
  const attrs: LogAttributes = {};
  if (userContext.uuid) attrs[ATTR_USER_ID] = userContext.uuid;
  if (userContext.ip) attrs[ATTR_CLIENT_ADDRESS] = userContext.ip;
  return attrs;
}

/**
 * Extracts trace context from the current active span for log correlation.
 * This enables linking logs to their corresponding traces in observability platforms.
 */
function getTraceContext(): LogAttributes {
  const span = trace.getActiveSpan();
  if (!span) return {};

  const ctx = span.spanContext();
  return {
    'trace.id': ctx.traceId,
    'span.id': ctx.spanId,
    'trace.flags': ctx.traceFlags,
  };
}

function emitLog(
  level: LogLevel,
  message: string,
  attributes?: LogAttributes,
  error?: Error,
): void {
  if (isEmittingLog) return;

  isEmittingLog = true;
  try {
    const { severity, text } = LOG_LEVELS[level];

    if (shouldLogToConsole()) {
      const consoleFn =
        console[level.toLowerCase() as 'debug' | 'info' | 'warn' | 'error'];
      const args: unknown[] = [`[${text}] ${message}`];
      if (error) args.push(error);
      if (attributes) args.push(attributes);
      consoleFn(...args);
    }

    if (!shouldUploadLog(severity)) {
      return;
    }

    const logAttributes: LogAttributes = {
      ...getTraceContext(),
      ...getUserContextAttributes(),
      ...attributes,
      ...(error && {
        'error.name': error.name,
        'error.message': error.message,
        'error.stack': error.stack,
      }),
    };

    getLogger().emit({
      severityNumber: severity,
      severityText: text,
      body: message,
      attributes: logAttributes,
      timestamp: Date.now(),
    });
  } finally {
    isEmittingLog = false;
  }
}

export const logger = {
  debug: (message: string, attributes?: LogAttributes) =>
    emitLog('DEBUG', message, attributes),

  info: (message: string, attributes?: LogAttributes) =>
    emitLog('INFO', message, attributes),

  warn: (message: string, attributes?: LogAttributes) =>
    emitLog('WARN', message, attributes),

  error: (message: string, error?: Error, attributes?: LogAttributes) =>
    emitLog('ERROR', message, attributes, error),
};

export function isLoggingReady(): boolean {
  return loggerProvider !== null;
}

export async function shutdownLogging(): Promise<void> {
  if (loggerProvider) {
    await loggerProvider.shutdown();
    loggerProvider = null;
  }
}

let exceptionErrorHandler: ((e: ErrorEvent) => void) | null = null;
let exceptionRejectionHandler: ((e: PromiseRejectionEvent) => void) | null =
  null;

export function setupExceptionLogging(): void {
  if (!isBrowser()) return;

  const recordException = (
    error: Error | string,
    source: 'onerror' | 'unhandledrejection',
    isCrossOrigin = false,
  ) => {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const errorType = errorObj?.name || 'Error';
    const errorMessage = errorObj?.message || String(error);

    logger.error(`[${source}] ${errorType}: ${errorMessage}`, errorObj, {
      'exception.type': errorType,
      'exception.escaped': true,
      'exception.source': source,
      'exception.is_cross_origin': isCrossOrigin,
      [ATTR_URL_FULL]: window.location.href,
      'app.error.severity':
        errorObj?.name === 'ValidationError' ? 'warning' : 'error',
      'custom.correlation.id': window.correlationId,
    });
  };

  exceptionErrorHandler = (event: ErrorEvent) => {
    const isCrossOrigin = !event.error && event.message === 'Script error.';
    recordException(
      event.error || event.message || 'Unknown error',
      'onerror',
      isCrossOrigin,
    );
  };

  exceptionRejectionHandler = (event: PromiseRejectionEvent) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));
    recordException(error, 'unhandledrejection');
  };

  window.addEventListener('error', exceptionErrorHandler);
  window.addEventListener('unhandledrejection', exceptionRejectionHandler);
}

export function cleanupExceptionLogging(): void {
  if (!isBrowser()) return;

  if (exceptionErrorHandler) {
    window.removeEventListener('error', exceptionErrorHandler);
    exceptionErrorHandler = null;
  }
  if (exceptionRejectionHandler) {
    window.removeEventListener('unhandledrejection', exceptionRejectionHandler);
    exceptionRejectionHandler = null;
  }
}

export function setupConsoleErrorInterception(): void {
  if (!isBrowser()) return;

  // Idempotent: restore first if already patched (e.g., HMR)
  if (originalConsoleError) {
    console.error = originalConsoleError;
  }

  originalConsoleError = console.error;

  console.error = (...args: unknown[]) => {
    originalConsoleError?.apply(console, args);

    if (isEmittingLog) return;

    try {
      const firstArg = args[0];
      let error: Error | undefined;
      let message: string;

      if (firstArg instanceof Error) {
        error = firstArg;
        message = error.message;
      } else {
        message = args
          .map((arg) => {
            if (typeof arg !== 'object') return String(arg);
            try {
              return JSON.stringify(arg);
            } catch {
              return '[Circular]';
            }
          })
          .join(' ');
        error = args.find((arg) => arg instanceof Error) as Error | undefined;
      }

      const otelLogger = getLogger('console');
      otelLogger.emit({
        severityNumber: LOG_LEVELS.ERROR.severity,
        severityText: LOG_LEVELS.ERROR.text,
        body: `[console.error] ${message}`,
        attributes: {
          ...getTraceContext(),
          ...getUserContextAttributes(),
          'log.source': 'console.error',
          ...(error && {
            'error.name': error.name,
            'error.message': error.message,
            'error.stack': error.stack,
          }),
          [ATTR_URL_FULL]: window.location.href,
        },
        timestamp: Date.now(),
      });
    } catch {
      // Silently fail to avoid infinite loop
    }
  };
}

export function cleanupConsoleErrorInterception(): void {
  if (originalConsoleError) {
    console.error = originalConsoleError;
    originalConsoleError = null;
  }
}
